import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AdvancedFilterButton,
  AdvancedFilterPanel,
  FilterPanelCollapse,
} from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { fiscalYearListQueryOptions } from '@/features/dcrApprove/api/fiscalYear.api';
import { zonalManagersQueryOptions } from '@/features/users/api/users.api';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { getFilterPanelProps } from '@/hooks/useUrlSyncedFilters';
import {
  getDateFromToFilterFields,
  getFiscalYearFilterField,
  getZonalManagerFilterField,
} from '@/utils/filterFields';
import { expenseApprovalsInfiniteQueryOptions } from '../api/billing.api';
import { ExpenseApprovalModal } from '../components/ExpenseApprovalModal';
import { getExpenseApprovalColumns } from '../components/ExpenseApprovalColumns';
import { BILLING_SEARCH_PLACEHOLDER } from '../constants/billing.constants';
import { useExpenseApprovalFilters } from '../hooks/useExpenseApprovalFilters';
import { getApprovalStatusFilterField } from '../utils/billingFilterFields';
import { getApprovalStatCards } from '../utils/billingStatCards';
import { useApproveExpense } from '../hooks/useApproveEntry';

export function ExpenseApprovePage() {
  const controller = useExpenseApprovalFilters();
  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(expenseApprovalsInfiniteQueryOptions(controller.params));
  const { data: zonalManagers } = useQuery(zonalManagersQueryOptions());
  const { data: fiscalYears } = useQuery(fiscalYearListQueryOptions());
  const approveExpense = useApproveExpense();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [openExpenseId, setOpenExpenseId] = useState<number | null>(null);

  const { search } = controller.params;
  const columns = useMemo(
    () => getExpenseApprovalColumns({ onOpen: setOpenExpenseId, searchText: search }),
    [search],
  );

  function handleDecide(accept: boolean) {
    if (openExpenseId === null) return;

    approveExpense.mutate(
      { expenseId: openExpenseId, payload: { accept } },
      { onSettled: () => setOpenExpenseId(null) },
    );
  }

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Expense Approve"
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={controller.params.dateFrom}
            dateTo={controller.params.dateTo}
          />
        }
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: BILLING_SEARCH_PLACEHOLDER,
        }}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <AdvancedFilterPanel
          fields={[
            ...getDateFromToFilterFields(),
            getZonalManagerFilterField(zonalManagers),
            getFiscalYearFilterField(fiscalYears),
            getApprovalStatusFilterField({ isClearable: false }),
          ]}
          {...getFilterPanelProps(controller)}
        />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the expenses"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid
            isLoading={isLoading}
            stats={getApprovalStatCards('expense', firstPageMeta)}
          />

          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            className="lg:flex-1"
            stickyColumnId="name"
            emptyMessage="No expenses for these filters"
            emptyHint="Try a wider date range."
            {...loadMoreProps}
          />
        </>
      )}

      <ExpenseApprovalModal
        expenseId={openExpenseId}
        isPending={approveExpense.isPending}
        onClose={() => setOpenExpenseId(null)}
        onDecide={handleDecide}
      />
    </div>
  );
}
